import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Search,
  Bell,
  User,
  Settings,
  Shield,
  LogOut,
  ChevronDown,
  FileText,
  CircleDollarSign,
  AlertCircle,
  FolderOpen,
  CheckCheck,
  Repeat,
  Menu
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = ({ isClientPortal = false, onMenuClick }) => {
  const { user, token, logout, isAdmin, isClient, getLinkedAccounts, switchAccount } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  // Sibling accounts (e.g. an investor account linked as a secondary email
  // on someone else's account, or a managed profile) — lets people switch
  // without logging out. Empty for the common single-account case.
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [switchingId, setSwitchingId] = useState(null);

  // Use a ref to always have the current token value in the interval callback
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Fetch notifications AND notices, unify them into one bell feed so the
  // counter matches what the investor sees on /notices. Without this the
  // two are out of sync — the bell would count personal notifications
  // (banking updates, etc.) but the /notices page only shows admin
  // broadcast notices → user reports "bell shows N but notices page is
  // empty".
  const fetchNotifications = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;
    const auth = { Authorization: `Bearer ${currentToken}` };
    try {
      const [notifRes, notifCountRes, noticesRes, noticesCountRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications?limit=10`, { headers: auth }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/notifications/unread-count`, { headers: auth }).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API_URL}/api/notices?limit=10`, { headers: auth }).catch(() => ({ data: { notices: [] } })),
        axios.get(`${API_URL}/api/notices/unread-count`, { headers: auth }).catch(() => ({ data: { unread_count: 0 } })),
      ]);

      // Normalise notices into the same shape the dropdown renders
      const noticeItems = (noticesRes.data?.notices || noticesRes.data || []).map(n => ({
        id: `notice:${n.id}`,
        notice_id: n.id,
        title: n.title || 'Notice',
        message: n.summary || n.subject || n.title || 'New notice',
        notification_type: 'notice',
        is_read: !!n.is_read,
        created_at: n.published_at || n.created_at,
        link: `/notices?open=${n.id}`,
        _source: 'notice',
      }));

      const notifItems = (notifRes.data || []).map(n => ({ ...n, _source: 'notification' }));

      const combined = [...notifItems, ...noticeItems]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 15);

      setNotifications(combined);
      setUnreadCount((notifCountRes.data?.count || 0) + (noticesCountRes.data?.unread_count || 0));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token, fetchNotifications]);

  useEffect(() => {
    if (!token) return;
    getLinkedAccounts().then(setLinkedAccounts).catch(() => setLinkedAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id]);

  const handleSwitchAccount = async (accountId) => {
    setSwitchingId(accountId);
    try {
      const switchedUser = await switchAccount(accountId);
      // Full reload (not a SPA navigate) so every page re-fetches under the
      // new identity instead of risking a stale/cross-account render.
      const target = switchedUser?.role === 'client' ? '/client'
        : switchedUser?.role === 'admin' ? '/admin'
        : '/dashboard';
      window.location.href = target;
    } catch (error) {
      console.error('Failed to switch account:', error);
      setSwitchingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      // Mark BOTH channels as read so the bell count zeroes out.
      await Promise.all([
        axios.put(`${API_URL}/api/notifications/mark-all-read`, {}, { headers }).catch(() => {}),
        axios.put(`${API_URL}/api/notices/mark-all-read`, {}, { headers }).catch(() => {}),
      ]);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read on the right backend depending on the source.
    if (!notification.is_read) {
      try {
        if (notification._source === 'notice') {
          // GET /notices/:id auto-marks-as-read server-side.
          await axios.get(`${API_URL}/api/notices/${notification.notice_id}`, { headers });
        } else {
          await axios.put(`${API_URL}/api/notifications/${notification.id}/read`, {}, { headers });
        }
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Navigate if link exists
    if (notification.link) {
      setNotifOpen(false);
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'statement':
        return <FileText className="h-4 w-4 text-blue-600" />;
      case 'distribution':
        return <CircleDollarSign className="h-4 w-4 text-green-600" />;
      case 'notice':
        return <AlertCircle className="h-4 w-4 text-amber-600" />;
      case 'document':
        return <FolderOpen className="h-4 w-4 text-purple-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-gray-100" data-testid="header">
      <div className="flex items-center justify-between h-full px-3 md:px-6 gap-2">
        {/* Mobile/tablet menu button — opens the off-canvas sidebar drawer */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-[#6C757D] hover:text-[#1D2D2A] flex-shrink-0"
          onClick={onMenuClick}
          data-testid="mobile-menu-button"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Search */}
        <div className="flex-1 max-w-md hidden sm:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#ADB5BD]" />
            <Input
              type="search"
              placeholder="Search investments, documents..."
              className="pl-10 h-10 bg-[#F8F9FA] border-0 focus:bg-white focus:ring-1 focus:ring-[#1D2D2A]"
              data-testid="header-search"
            />
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 md:gap-4 ml-auto">
          {/* Admin previewing the investor UI (their own account has no
              real investor/client sibling to switch into instead) — click
              to exit back to the admin portal and clear the preview flag,
              rather than getting bounced there by clicking any other link. */}
          {isAdmin && (
            <Badge
              className="bg-[#1D2D2A] text-white cursor-pointer hover:bg-[#243D37]"
              data-testid="admin-badge"
              onClick={() => {
                try { sessionStorage.removeItem('admin_preview_mode'); } catch { /* ignore */ }
                navigate('/admin');
              }}
            >
              Admin — Exit preview
            </Badge>
          )}
          
          {/* Client Badge */}
          {isClient && (
            <Badge className="bg-[#0D3B66] text-white" data-testid="client-badge">
              Client
            </Badge>
          )}

          {/* Notifications */}
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative text-[#6C757D] hover:text-[#1D2D2A]"
                data-testid="notifications-button"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#DC3545] text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-96" data-testid="notifications-dropdown">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <DropdownMenuLabel className="p-0 text-base font-semibold">Notifications</DropdownMenuLabel>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-[#18A37C] hover:text-[#1D2D2A] h-auto py-1"
                    onClick={handleMarkAllRead}
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="h-3 w-3 mr-1" />
                    Mark all read
                  </Button>
                )}
              </div>
              
              <ScrollArea className="max-h-[400px]">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center text-[#6C757D]">
                    <Bell className="h-8 w-8 mx-auto mb-2 text-[#ADB5BD]" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 transition-colors ${
                        !notification.is_read ? 'bg-blue-50/50' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                      data-testid={`notification-${notification.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notification.notification_type === 'statement' ? 'bg-blue-100' :
                          notification.notification_type === 'distribution' ? 'bg-green-100' :
                          notification.notification_type === 'notice' ? 'bg-amber-100' :
                          notification.notification_type === 'document' ? 'bg-purple-100' :
                          'bg-gray-100'
                        }`}>
                          {getNotificationIcon(notification.notification_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${!notification.is_read ? 'text-[#212529]' : 'text-[#6C757D]'}`}>
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <span className="w-2 h-2 bg-[#1D2D2A] rounded-full flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          <p className="text-xs text-[#6C757D] mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-[#ADB5BD] mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </ScrollArea>
              
              {notifications.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="justify-center text-[#18A37C] cursor-pointer"
                    onClick={() => {
                      setNotifOpen(false);
                      navigate('/notices');
                    }}
                  >
                    View all notifications
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2 text-[#212529] hover:bg-[#F8F9FA]"
                data-testid="user-menu-button"
              >
                <div className="w-8 h-8 rounded-full bg-[#1D2D2A] flex items-center justify-center text-white text-sm font-medium">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <span className="hidden md:inline font-medium">{user?.first_name}</span>
                <ChevronDown className="h-4 w-4 text-[#6C757D]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-[#6C757D] font-normal">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-profile">
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/kyc')} data-testid="menu-security">
                <Shield className="mr-2 h-4 w-4" />
                Security
              </DropdownMenuItem>
              {linkedAccounts.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-normal text-[#6C757D] py-1">
                    Switch account
                  </DropdownMenuLabel>
                  {linkedAccounts.map((acct) => (
                    <DropdownMenuItem
                      key={acct.id}
                      onClick={() => handleSwitchAccount(acct.id)}
                      disabled={switchingId === acct.id}
                      data-testid={`switch-account-${acct.id}`}
                    >
                      <Repeat className="mr-2 h-4 w-4 text-[#6C757D]" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">
                          {acct.full_name || `${acct.first_name} ${acct.last_name}`}
                        </span>
                        <span className="text-xs text-[#6C757D] capitalize">
                          {acct.role}{acct.risk_participant_code ? ` · RP: ${acct.risk_participant_code}` : ''}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-[#DC3545]" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
